import * as React from "react"
import { format, addDays, isBefore, startOfDay } from "date-fns"
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"

export function DateRangePicker({ 
  checkInDate, 
  checkOutDate, 
  onCheckInChange, 
  onCheckOutChange,
  minDate = new Date(),
  className 
}) {
  const [isCheckInOpen, setIsCheckInOpen] = React.useState(false)
  const [isCheckOutOpen, setIsCheckOutOpen] = React.useState(false)
  const [currentMonth, setCurrentMonth] = React.useState(new Date())

  const today = startOfDay(new Date())
  const tomorrow = addDays(today, 1)

  // Set defaults if not provided
  React.useEffect(() => {
    if (!checkInDate) {
      onCheckInChange(today)
    }
    if (!checkOutDate) {
      onCheckOutChange(tomorrow)
    }
  }, [])

  const handleCheckInSelect = (date) => {
    onCheckInChange(date)
    // If checkout is before or same as checkin, set checkout to next day
    if (!checkOutDate || isBefore(checkOutDate, addDays(date, 1)) || checkOutDate.getTime() === date.getTime()) {
      onCheckOutChange(addDays(date, 1))
    }
    setIsCheckInOpen(false)
    setIsCheckOutOpen(true)
  }

  const handleCheckOutSelect = (date) => {
    onCheckOutChange(date)
    setIsCheckOutOpen(false)
  }

  const getDaysInMonth = (date) => {
    const year = date.getFullYear()
    const month = date.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const daysInMonth = lastDay.getDate()
    const startingDayOfWeek = firstDay.getDay()
    
    const days = []
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null)
    }
    // Add the days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i))
    }
    return days
  }

  const isDateDisabled = (date, isCheckOut = false) => {
    if (!date) return true
    const compareDate = startOfDay(date)
    if (isCheckOut) {
      // For checkout, date must be after checkin
      return isBefore(compareDate, checkInDate ? addDays(startOfDay(checkInDate), 1) : tomorrow)
    }
    // For checkin, date must be today or later
    return isBefore(compareDate, today)
  }

  const isDateInRange = (date) => {
    if (!date || !checkInDate || !checkOutDate) return false
    const d = startOfDay(date)
    return d >= startOfDay(checkInDate) && d <= startOfDay(checkOutDate)
  }

  const isCheckInDate = (date) => {
    if (!date || !checkInDate) return false
    return startOfDay(date).getTime() === startOfDay(checkInDate).getTime()
  }

  const isCheckOutDate = (date) => {
    if (!date || !checkOutDate) return false
    return startOfDay(date).getTime() === startOfDay(checkOutDate).getTime()
  }

  const CalendarView = ({ isCheckOut = false, onSelect }) => {
    const days = getDaysInMonth(currentMonth)
    const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    
    return (
      <div className="p-4 bg-white">
        <div className="flex items-center justify-between mb-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))}
            className="text-slate-700 hover:text-navy hover:bg-slate-100"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <span className="font-semibold text-slate-900">
            {format(currentMonth, 'MMMM yyyy')}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))}
            className="text-slate-700 hover:text-navy hover:bg-slate-100"
          >
            <ChevronRight className="h-5 w-5" />
          </Button>
        </div>
        
        <div className="grid grid-cols-7 gap-1 mb-2">
          {weekDays.map(day => (
            <div key={day} className="text-center text-xs font-medium text-slate-700 py-1">
              {day}
            </div>
          ))}
        </div>
        
        <div className="grid grid-cols-7 gap-1">
          {days.map((date, index) => {
            if (!date) {
              return <div key={`empty-${index}`} className="h-9" />
            }
            
            const disabled = isDateDisabled(date, isCheckOut)
            const inRange = isDateInRange(date)
            const isStart = isCheckInDate(date)
            const isEnd = isCheckOutDate(date)
            const isToday = startOfDay(date).getTime() === today.getTime()
            
            return (
              <button
                key={date.toISOString()}
                onClick={() => !disabled && onSelect(date)}
                disabled={disabled}
                className={cn(
                  "h-9 w-9 rounded-md text-sm font-medium transition-colors",
                  disabled && "text-slate-300 cursor-not-allowed bg-slate-50",
                  !disabled && "hover:bg-navy-100 text-slate-700 cursor-pointer",
                  isToday && !isStart && !isEnd && "border border-gold",
                  inRange && !isStart && !isEnd && "bg-navy-50",
                  (isStart || isEnd) && "bg-navy-600 text-white hover:bg-navy-700 cursor-pointer",
                )}
              >
                {date.getDate()}
              </button>
            )
          })}
        </div>
        
        {/* Quick Select Options */}
        <div className="mt-4 pt-4 border-t flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              if (isCheckOut) {
                onSelect(addDays(checkInDate || today, 1))
              } else {
                onSelect(today)
              }
            }}
            className="text-xs"
          >
            {isCheckOut ? '+1 Night' : 'Today'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              if (isCheckOut) {
                onSelect(addDays(checkInDate || today, 2))
              } else {
                onSelect(tomorrow)
              }
            }}
            className="text-xs"
          >
            {isCheckOut ? '+2 Nights' : 'Tomorrow'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              if (isCheckOut) {
                onSelect(addDays(checkInDate || today, 3))
              } else {
                onSelect(addDays(today, 7))
              }
            }}
            className="text-xs"
          >
            {isCheckOut ? '+3 Nights' : 'Next Week'}
          </Button>
        </div>
      </div>
    )
  }

  const nightsCount = checkInDate && checkOutDate 
    ? Math.ceil((checkOutDate - checkInDate) / (1000 * 60 * 60 * 24))
    : 1

  return (
    <div className={cn("flex flex-col sm:flex-row gap-2", className)}>
      {/* Check-in Date */}
      <Popover open={isCheckInOpen} onOpenChange={setIsCheckInOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              "flex-1 justify-start text-left font-normal h-14 border-slate-300 hover:border-navy-500",
              !checkInDate && "text-slate-700"
            )}
          >
            <div className="flex items-center gap-3">
              <CalendarIcon className="h-5 w-5 text-navy-600" />
              <div>
                <p className="text-xs text-slate-700 uppercase font-semibold">Check-in</p>
                <p className="text-sm font-medium text-slate-900">
                  {checkInDate ? format(checkInDate, "EEE, dd MMM yyyy") : "Select date"}
                </p>
              </div>
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 bg-white border border-slate-200 shadow-xl" align="start">
          <CalendarView onSelect={handleCheckInSelect} />
        </PopoverContent>
      </Popover>

      {/* Nights Indicator */}
      <div className="hidden sm:flex items-center justify-center px-3">
        <div className="text-center">
          <p className="text-xs text-slate-700 uppercase font-semibold">Nights</p>
          <p className="text-lg font-bold text-navy-600">{nightsCount}</p>
        </div>
      </div>

      {/* Check-out Date */}
      <Popover open={isCheckOutOpen} onOpenChange={setIsCheckOutOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              "flex-1 justify-start text-left font-normal h-14 border-slate-300 hover:border-navy-500",
              !checkOutDate && "text-slate-700"
            )}
          >
            <div className="flex items-center gap-3">
              <CalendarIcon className="h-5 w-5 text-navy-600" />
              <div>
                <p className="text-xs text-slate-700 uppercase font-semibold">Check-out</p>
                <p className="text-sm font-medium text-slate-900">
                  {checkOutDate ? format(checkOutDate, "EEE, dd MMM yyyy") : "Select date"}
                </p>
              </div>
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 bg-white border border-slate-200 shadow-xl" align="start">
          <CalendarView isCheckOut onSelect={handleCheckOutSelect} />
        </PopoverContent>
      </Popover>
    </div>
  )
}

export default DateRangePicker
